import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { generateSolanaWalletSecret, tryPubkeyFromSolanaWalletSecret } from '@/lib/solanaWalletSecret';

export interface WalletAccount {
  id: string;
  name: string;
  secretKey: string;
  publicKey: string;
  profilePicture?: string;
}

export interface XProfile {
  id: string;
  username: string;
  displayName: string;
  connectedAt: string;
}

export interface SubWalletConfig {
  id: string;
  publicKey: string;
  secretKey: string;
  allocSol?: number;
  slippageBps?: number;
}

interface WalletContextState {
  publicKey: string | null;
  secretKey: string | null;
  connected: boolean;
  connecting: boolean;
  accounts: WalletAccount[];
  activeAccountId: string | null;
  xProfile: XProfile | null;
  connectX: () => boolean;
  disconnectX: () => void;
  connect: () => Promise<void>;
  disconnect: () => Promise<void>;
  createAccount: (name?: string) => boolean;
  switchAccount: (id: string) => void;
  importAccount: (secretKey: string, name?: string) => boolean;
  updateAccount: (id: string, updates: Partial<WalletAccount>) => void;
  subWallets: SubWalletConfig[];
  setSubWallets: React.Dispatch<React.SetStateAction<SubWalletConfig[]>>;
  createSubWallets: (count: number) => void;
  updateSubWallet: (id: string, updates: Partial<SubWalletConfig>) => void;
  clearSubWallets: () => void;
}

const WalletContext = createContext<WalletContextState>({
  publicKey: null,
  secretKey: null,
  connected: false,
  connecting: false,
  accounts: [],
  activeAccountId: null,
  xProfile: null,
  connectX: () => false,
  disconnectX: () => {},
  connect: async () => {},
  disconnect: async () => {},
  createAccount: () => false,
  switchAccount: () => {},
  importAccount: () => false,
  updateAccount: () => {},
  subWallets: [],
  setSubWallets: () => {},
  createSubWallets: () => {},
  updateSubWallet: () => {},
  clearSubWallets: () => {},
});

export const useWallet = () => useContext(WalletContext);

const LS_INTERNAL_WALLET_SECRET = "unt_internal_wallet_secret_v1";
const LS_ACCOUNTS = "unt_internal_wallet_accounts_v2";
const LS_ACTIVE_ID = "unt_internal_wallet_active_id_v2";
const LS_X_PROFILE = "unt_x_profile_v1";
const LS_SUB_WALLETS = "unt_sub_wallets_v1";

export function WalletProvider({ children }: { children: React.ReactNode }) {
  const [accounts, setAccounts] = useState<WalletAccount[]>([]);
  const [activeAccountId, setActiveAccountId] = useState<string | null>(null);
  const [xProfile, setXProfile] = useState<XProfile | null>(null);
  const [connected, setConnected] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [subWalletsState, setSubWalletsState] = useState<SubWalletConfig[]>([]);

  const setSubWallets = useCallback((value: React.SetStateAction<SubWalletConfig[]>) => {
    setSubWalletsState(prev => {
      const next = typeof value === 'function' ? value(prev) : value;
      localStorage.setItem(LS_SUB_WALLETS, JSON.stringify(next));
      return next;
    });
  }, []);

  const activeAccount = useMemo(() => accounts.find(a => a.id === activeAccountId), [accounts, activeAccountId]);
  const publicKey = activeAccount?.publicKey || null;
  const secretKey = activeAccount?.secretKey || null;

  useEffect(() => {
    try {
      const storedX = localStorage.getItem(LS_X_PROFILE);
      if (storedX) {
        const parsed = JSON.parse(storedX) as XProfile;
        if (parsed?.username) {
          setXProfile(parsed);
        }
      }
    } catch {
      // Ignore X profile parse errors
    }

    try {
      const storedSub = localStorage.getItem(LS_SUB_WALLETS);
      if (storedSub) {
        setSubWalletsState(JSON.parse(storedSub));
      }
    } catch {}

    // Load accounts
    try {
      const stored = localStorage.getItem(LS_ACCOUNTS);
      if (stored) {
        const parsed = JSON.parse(stored) as WalletAccount[];
        if (Array.isArray(parsed) && parsed.length > 0) {
          setAccounts(parsed);
          const storedActiveId = localStorage.getItem(LS_ACTIVE_ID);
          if (storedActiveId && parsed.some(a => a.id === storedActiveId)) {
            setActiveAccountId(storedActiveId);
          } else {
            setActiveAccountId(parsed[0].id);
          }
          setConnected(true);
          return; // already loaded from v2
        }
      }
    } catch {
      // Parse error
    }

    // Fallback to v1
    const existingSecret = localStorage.getItem(LS_INTERNAL_WALLET_SECRET);
    if (existingSecret) {
      const pubkey = tryPubkeyFromSolanaWalletSecret(existingSecret);
      if (pubkey) {
        const acc: WalletAccount = {
          id: "account-1",
          name: "Account 1",
          secretKey: existingSecret,
          publicKey: pubkey,
        };
        setAccounts([acc]);
        setActiveAccountId(acc.id);
        localStorage.setItem(LS_ACCOUNTS, JSON.stringify([acc]));
        localStorage.setItem(LS_ACTIVE_ID, acc.id);
        setConnected(true);
      }
    }

    // Check backend session
    fetch("/api/auth/me")
      .then(res => res.json())
      .then(data => {
        if (data && data.user) {
          setXProfile({
            id: data.user.id,
            username: data.user.handle,
            displayName: data.user.displayName,
            connectedAt: new Date(data.user.createdAt).toISOString(),
          });
          localStorage.setItem(LS_X_PROFILE, JSON.stringify({
            id: data.user.id,
            username: data.user.handle,
            displayName: data.user.displayName,
            connectedAt: new Date(data.user.createdAt).toISOString(),
          }));
        }
      })
      .catch(() => {});
  }, []);

  const connectX = useCallback(() => {
    const width = 600;
    const height = 600;
    const left = window.screenX + (window.innerWidth - width) / 2;
    const top = window.screenY + (window.innerHeight - height) / 2;
    window.open('/api/auth/x/login', 'Login with X', `width=${width},height=${height},left=${left},top=${top}`);

    const handleMessage = async (e: MessageEvent) => {
      if (e.data === "x_auth_success") {
        window.removeEventListener("message", handleMessage);
        try {
          const res = await fetch("/api/auth/me");
          const data = await res.json();
          if (data && data.user) {
            const profile = {
              id: data.user.id,
              username: data.user.handle,
              displayName: data.user.displayName,
              connectedAt: new Date(data.user.createdAt).toISOString(),
            };
            setXProfile(profile);
            localStorage.setItem(LS_X_PROFILE, JSON.stringify(profile));

            setAccounts(prev => {
              const updated = prev.map((account, index) => {
                const genericName = account.name?.startsWith("Account") || account.name?.startsWith("Imported") || account.name?.trim().length === 0;
                return genericName
                  ? { ...account, name: `${profile.displayName}'s Wallet ${index + 1}` }
                  : account;
              });
              localStorage.setItem(LS_ACCOUNTS, JSON.stringify(updated));

              // Sync all existing local wallets to the backend now that X is connected
              updated.forEach((acc) => {
                fetch("/api/wallets", {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({ publicKey: acc.publicKey, name: acc.name })
                }).catch(() => {});
              });

              return updated;
            });
          }
        } catch (err) {
          console.error("Auth me check failed", err);
        }
      }
    };
    window.addEventListener("message", handleMessage);
    return true;
  }, [accounts]);

  const disconnectX = useCallback(() => {
    setXProfile(null);
    localStorage.removeItem(LS_X_PROFILE);
  }, []);

  const createAccount = useCallback((name?: string) => {
    const { secret, pubkey } = generateSolanaWalletSecret();
    const accName = name || `Account ${accounts.length + 1}`;
    const newAcc: WalletAccount = {
      id: `account-${Date.now()}`,
      name: accName,
      secretKey: secret,
      publicKey: pubkey,
    };

    const updated = [...accounts, newAcc];
    setAccounts(updated);
    localStorage.setItem(LS_ACCOUNTS, JSON.stringify(updated));
    setActiveAccountId(newAcc.id);
    localStorage.setItem(LS_ACTIVE_ID, newAcc.id);
    setConnected(true);

    // Sync to backend non-blocking if X is connected
    if (xProfile) {
      fetch("/api/wallets", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ publicKey: pubkey, name: accName })
      }).catch(() => {});
    }

    return true;
  }, [accounts, xProfile]);

  const importAccount = useCallback((importedSecret: string, name?: string) => {
    const pubkey = tryPubkeyFromSolanaWalletSecret(importedSecret);
    if (!pubkey) return false;
    let success = false;
    setAccounts(prev => {
      if (prev.some(a => a.publicKey === pubkey)) return prev;
      const accName = name || `Imported Account`;
      const newAcc: WalletAccount = {
        id: `account-${Date.now()}`,
        name: accName,
        secretKey: importedSecret,
        publicKey: pubkey,
      };
      const updated = [...prev, newAcc];
      localStorage.setItem(LS_ACCOUNTS, JSON.stringify(updated));
      success = true;

      if (xProfile) {
        fetch("/api/wallets", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ publicKey: pubkey, name: accName })
        }).catch(() => {});
      }

      return updated;
    });
    return success;
  }, [xProfile]);

  const switchAccount = useCallback((id: string) => {
    if (accounts.some(a => a.id === id)) {
      setActiveAccountId(id);
      localStorage.setItem(LS_ACTIVE_ID, id);
    }
  }, [accounts]);

  const updateAccount = useCallback((id: string, updates: Partial<WalletAccount>) => {
    setAccounts(prev => {
      const updated = prev.map(a => a.id === id ? { ...a, ...updates } : a);
      localStorage.setItem(LS_ACCOUNTS, JSON.stringify(updated));
      return updated;
    });
  }, []);

  const createSubWallets = useCallback((count: number) => {
    const newWallets: SubWalletConfig[] = [];
    for (let i = 0; i < count; i++) {
      const { secret, pubkey } = generateSolanaWalletSecret();
      newWallets.push({
        id: `sub-${Date.now()}-${i}`,
        publicKey: pubkey,
        secretKey: secret,
      });
    }
    setSubWallets(prev => [...prev, ...newWallets]);
  }, [setSubWallets]);

  const updateSubWallet = useCallback((id: string, updates: Partial<SubWalletConfig>) => {
    setSubWallets(prev => prev.map(w => w.id === id ? { ...w, ...updates } : w));
  }, [setSubWallets]);

  const clearSubWallets = useCallback(() => {
    setSubWallets([]);
  }, [setSubWallets]);

  const connect = useCallback(async () => {
    setConnecting(true);
    try {
      if (accounts.length === 0) {
        const { secret, pubkey } = generateSolanaWalletSecret();
        const acc: WalletAccount = {
          id: `account-${Date.now()}`,
          name: "Account 1",
          secretKey: secret,
          publicKey: pubkey,
        };
        setAccounts([acc]);
        setActiveAccountId(acc.id);
        localStorage.setItem(LS_ACCOUNTS, JSON.stringify([acc]));
        localStorage.setItem(LS_ACTIVE_ID, acc.id);
      }
      setConnected(true);
    } catch (err) {
      console.error("Internal Wallet connect error:", err);
    } finally {
      setConnecting(false);
    }
  }, [accounts, xProfile]);

  const disconnect = useCallback(async () => {
    setConnected(false);
  }, []);

  return (
    <WalletContext.Provider value={{ 
      publicKey, secretKey, connected, connecting, accounts, activeAccountId, xProfile, 
      connectX, disconnectX, connect, disconnect, createAccount, switchAccount, importAccount, updateAccount,
      subWallets: subWalletsState, setSubWallets, createSubWallets, updateSubWallet, clearSubWallets
    }}>
      {children}
    </WalletContext.Provider>
  );
}
